import moment from 'moment'

import Icons from '../../components/common/icons'
import { getNestedProperty } from '../../utilities/parsers'
import { getFileName } from '../organisation/common/commonUtils'

const defaultColumnProps = {
  sortable: false,
  resizable: true,
  isVisible: true,
}

export const getColumns = ({ onViewAction }: any) => {
  const createRenderCell =
    (key: string | string[], type?: string) => (row: any) => {
      switch (type) {
        case 'join':
          return {
            cell: `${getNestedProperty(row, key[0]) ?? ''} ${
              getNestedProperty(row, key[1]) ?? ''
            }`,
          }
        case 'icons':
          return {
            cell: (
              <div className="flex gap-1 items-center">
                {row?.company?.primary_contact &&
                  row?.company?.primary_contact?.id === row.customer_id && (
                    <Icons name="favourite-colored" />
                  )}
                <>
                  {`${getNestedProperty(row, key[0]) ?? ''} ${
                    getNestedProperty(row, key[1]) ?? ''
                  }`}
                </>
              </div>
            ),
          }
        case 'fulldate':
          const propertyValue = getNestedProperty(row, key as string)

          return {
            cell: (
              <>
                {propertyValue
                  ? moment(propertyValue).format('DD-MM-YYYY')
                  : ''}
              </>
            ),
            toolTip: getNestedProperty(row, key as string) ?? '',
          }

        case 'createddate':
          return {
            cell: (
              <>
                {row?.uploaded_at
                  ? moment(row?.uploaded_at).format('DD-MM-YYYY')
                  : '--'}
              </>
            ),
          }
        case 'lastlogin':
          return {
            cell: (
              <>
                {row?.user?.last_login
                  ? moment(row?.user?.last_login).format('DD-MM-YYYY')
                  : ''}
              </>
            ),
          }
        case 'file':
          return {
            cell: <>{getFileName(row?.full_url)}</>,
          }
        case 'fullname':
          return {
            cell: <>{`${row?.user?.first_name} ${row?.user?.last_name}`}</>,
          }
        case 'custom':
          return {
            cell: (
              <div className="flex gap-1 items-center">
                {getNestedProperty(row, key[0]) && (
                  <Icons
                    name="phone"
                    toolTip={getNestedProperty(row, key[0])}
                  />
                )}
                {getNestedProperty(row, key[1]) && (
                  <Icons
                    name="email"
                    toolTip={getNestedProperty(row, key[1])}
                  />
                )}
              </div>
            ),
          }
        default:
          return {
            cell: getNestedProperty(row, key as string),
            toolTip: getNestedProperty(row, key as string) ?? '',
          }
      }
    }

  const column = [
    {
      title: 'Name',
      field: 'first_name',
      ...defaultColumnProps,
      fixed: true,
      renderCell: createRenderCell('user.first_name', 'fullname'),
      customCell: true,
      link: true,
      rowClick: (row: any) => onViewAction(row),
      sortKey: 'user__first_name',
    },

    {
      title: 'Organisation Name',
      field: 'organisation.name',
      renderCell: createRenderCell('organisation.name'),
      customCell: true,
      ...defaultColumnProps,
      sortKey: 'organisation__name',
    },
    {
      title: 'Job Title',
      field: 'job_title',
      // customCell: true,
      ...defaultColumnProps,
      sortKey: 'user__job_title',
    },
    // {
    //   title: 'Number',
    //   field: 'contact_number',
    //   // customCell: true,
    //   ...defaultColumnProps,
    // },
    {
      title: 'Email',
      renderCell: createRenderCell('user.username'),
      field: 'username',
      customCell: true,
      ...defaultColumnProps,
      sortKey: 'user__email',
    },
    {
      title: 'Phone Number',
      field: 'phone',
      renderCell: createRenderCell('contact_number'),
      customCell: true,
      ...defaultColumnProps,
    },
    {
      title: 'Status',
      field: 'status',
      renderCell: createRenderCell('user.status'),
      ...defaultColumnProps,
      customCell: true,
      sortKey: 'user__status',
    },
    {
      title: 'Last Login',
      field: 'last_login_days_ago',
      renderCell: createRenderCell('user.last_login_days_ago'),
      // type: 'date',
      customCell: true,
      ...defaultColumnProps,
      sortKey: 'user__last_login',
    },

    {
      title: 'Created At',
      field: 'datetime_created',
      renderCell: createRenderCell('user.datetime_created', 'fulldate'),
      customCell: true,
      ...defaultColumnProps,
      type: 'date',
    },
    {
      title: 'Updated At',
      field: 'datetime_updated',
      renderCell: createRenderCell('user.datetime_updated', 'fulldate'),
      customCell: true,
      ...defaultColumnProps,
      type: 'date',
    },
    {
      title: 'Main Contact',
      field: 'main_contact',
      renderCell: (row: any) => {
        const isMainContact =
          row?.user?.main_contact === true ||
          row?.user?.main_contact === 1 ||
          row?.user?.main_contact === 'true'
        return {
          cell: isMainContact ? 'Yes' : 'No',
          toolTip: isMainContact ? 'Yes' : 'No',
        }
      },
      customCell: true,
      ...defaultColumnProps,
    },
    {
      title: 'CEO Chair',
      field: 'ceo_chair_contact',
      renderCell: (row: any) => {
        const isCeoChair =
          row?.user?.ceo_chair_contact === true ||
          row?.user?.ceo_chair_contact === 1 ||
          row?.user?.ceo_chair_contact === 'true'
        return {
          cell: isCeoChair ? 'Yes' : 'No',
          toolTip: isCeoChair ? 'Yes' : 'No',
        }
      },
      customCell: true,
      ...defaultColumnProps,
    },
    {
      title: 'Main Contact First Name',
      field: 'main_contact_first_name',
      renderCell: (row: any) => {
        const firstName = row?.organisation?.main_contact?.first_name || ''
        return {
          cell: `${firstName}`.trim(),
          toolTip: `${firstName}`.trim(),
        }
      },
      sortKey: 'main_contact__first_name',
      ...defaultColumnProps,
      customCell: true,
    },
    {
      title: 'Main Contact Last Name',
      field: 'main_contact_last_name',
      renderCell: (row: any) => {
        const lastName = row?.organisation?.main_contact?.last_name || ''
        return {
          cell: `${lastName}`.trim(),
          toolTip: `${lastName}`.trim(),
        }
      },
      sortKey: 'main_contact__last_name',
      ...defaultColumnProps,
      customCell: true,
    },
    {
      title: 'CEO/Chair First Name',
      field: 'ceo_contact_first_name',
      renderCell: (row: any) => {
        const firstName = row?.organisation?.ceo_contact?.first_name || ''
        return {
          cell: `${firstName}`.trim(),
          toolTip: `${firstName}`.trim(),
        }
      },
      sortKey: 'ceo_contact__first_name',
      ...defaultColumnProps,
      customCell: true,
    },
    {
      title: 'CEO/Chair Last Name',
      field: 'ceo_contact_last_name',
      renderCell: (row: any) => {
        const lastName = row?.organisation?.ceo_contact?.last_name || ''
        return {
          cell: `${lastName}`.trim(),
          toolTip: `${lastName}`.trim(),
        }
      },
      sortKey: 'ceo_contact__first_name',
      ...defaultColumnProps,
      customCell: true,
    },
  ]

  return column
}
